import { FARM_DIAGNOSTICS_PRACTICE_LABELS } from 'constants/uiConstants';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IFarm } from 'interfaces/IFarm';
import { convertToHectares } from 'utils/unitConversion';
import {
  classifyTrend,
  EXCLUSIVE_PRACTICE_TAGS,
  resolveYieldKgPerHa,
  computeFinancialWue,
  computeWueIndex,
  type TrendDirection,
} from './computeFarmDiagnosticsMetrics';
import type { WueTrendYearRow } from './computeWueTrend';
import type { FarmYearInput, PracticeTag } from '../../../../redux/slices/InsightsSlice';

/**
 * One paddock as Rotation Efficiency will show it — resolved once per scope
 * change so callers don't re-derive the farm-id-per-paddock lookup on every
 * render.
 */
export interface RotationPaddock {
  paddockId: string;
  paddockName: string;
  farmId: string;
  areaHa: number;
}

export interface RotationScope {
  /** Farm name for Farm scope, or `orgName` for Org scope. */
  label: string;
  paddocks: RotationPaddock[];
}

/**
 * Resolves which paddocks Rotation Efficiency shows. `selectedFarmId` is
 * already correctly resolved to the tree's parent farm for Paddock/Plan
 * selections (or null for Org/Crop selections) by useFarmsNavSync.ts — no
 * further tree-walking is needed here, just branch on it directly.
 */
export const resolveRotationScope = (
  farms: IFarm[],
  selectedFarmId: string | null,
  orgName: string,
  gUnits: GUnit[]
): RotationScope => {
  const toRotationPaddock = (farm: IFarm) =>
    farm.paddocks
      .filter((p) => p.isActive)
      .map((p) => ({
        paddockId: p.paddockId,
        paddockName: p.paddockName,
        farmId: farm.id,
        areaHa:
          p.paddockArea > 0
            ? (convertToHectares(p.paddockArea, p.areaUnit, gUnits) ?? p.paddockArea)
            : 0,
      }));

  if (selectedFarmId) {
    const farm = farms.find((f) => f.id === selectedFarmId);
    if (!farm) return { label: orgName, paddocks: [] };
    return { label: farm.farmName, paddocks: toRotationPaddock(farm) };
  }

  return {
    label: orgName,
    paddocks: farms.flatMap((farm) => toRotationPaddock(farm)),
  };
};

export type RotationExcludedReason =
  | 'noData'
  | 'fallow'
  | 'pasture'
  | 'missingRainfall';

export interface RotationYearNote {
  year: number;
  reason: RotationExcludedReason;
}

export interface RotationRow {
  paddockId: string;
  paddockName: string;
  areaHa: number;
  /** Distinct practice labels across the eligible years in range — empty if none tagged. */
  managementLabels: string[];
  /** Years excluded from the averages, with why — a row is never dropped, just annotated. */
  excludedYears: RotationYearNote[];
  rotationYieldKgPerHa: number | null;
  rotationGmPerHa: number | null;
  /**
   * Null whenever rotationYieldKgPerHa is null, OR any eligible (non-fallow/
   * pasture, has-yield-data) year in range is missing rainfall — never a
   * partial average computed silently over whichever years happen to have
   * rainfall entered.
   */
  rotationWue: number | null;
  /**
   * Average gross margin ($/ha) ÷ average rainfall (mm) — the financial
   * counterpart of rotationWue, and the fair comparison across farms with
   * different rainfall. Null under the same rule: no GM, or any eligible year
   * missing rainfall.
   */
  rotationFinancialWue: number | null;
  /** WUE for the earlier half of the selected range's distinct years — null whenever trend is (see trendUnavailableReason). */
  earlyPeriodWue: number | null;
  /** WUE for the later half of the selected range's distinct years — null whenever trend is (see trendUnavailableReason). */
  latePeriodWue: number | null;
  trend: TrendDirection | null;
  /** Set only when rotationWue exists but trend couldn't be computed — explains why, for the table's tooltip. Null whenever a trend WAS computed, or rotationWue itself is null (a different tooltip already covers that case). */
  trendUnavailableReason: TrendUnavailableReason | null;
}

/**
 * Why `trend` came back null despite `rotationWue` being available:
 * - `rangeTooShort` — the selected range spans fewer than 2 distinct years,
 *   so there's nothing to split into an earlier/later half to compare.
 * - `insufficientData` — the range has 2+ years, but one half has no
 *   computable WUE (missing rainfall, or fallow/pasture-only years) — see
 *   this row's own `excludedYears` for exactly which years and why.
 */
export type TrendUnavailableReason =
  | { kind: 'rangeTooShort' }
  | { kind: 'insufficientData' };

interface EligibleYearAccumulator {
  yieldKgPerHaSum: number;
  yieldKgPerHaCount: number;
  gmPerHaSum: number;
  gmPerHaCount: number;
  rainfallSum: number;
  rainfallCount: number;
  missingRainfallYears: number[];
  managementTags: Set<PracticeTag>;
}

const accumulateEligibleYear = (
  acc: EligibleYearAccumulator,
  row: WueTrendYearRow,
  base: NonNullable<WueTrendYearRow['actual']>,
  areaHa: number,
  farmYear: FarmYearInput | undefined,
  gUnits: GUnit[]
): void => {
  const yieldKgPerHa = resolveYieldKgPerHa(base.yieldValue, base.yieldUnit, gUnits);
  if (yieldKgPerHa != null) {
    acc.yieldKgPerHaSum += yieldKgPerHa;
    acc.yieldKgPerHaCount += 1;
  }

  if (areaHa > 0) {
    acc.gmPerHaSum += base.grossMarginTotal / areaHa;
    acc.gmPerHaCount += 1;
  }

  const rainfallMm = farmYear?.rainfallMm ?? null;
  if (rainfallMm != null) {
    acc.rainfallSum += rainfallMm;
    acc.rainfallCount += 1;
  } else {
    acc.missingRainfallYears.push(row.year);
  }

  for (const tag of farmYear?.practiceTags ?? []) {
    acc.managementTags.add(tag);
  }
};

/**
 * Aggregates one paddock's already time-range-filtered Epic 1 rows into its
 * Rotation Efficiency figures. `farmYearInputs` is that paddock's FARM's
 * rainfall/practice data (farm-level — the same value applies to every
 * paddock under that farm, per product decision), keyed by year.
 */
export const computeRotationRow = (
  paddockId: string,
  paddockName: string,
  areaHa: number,
  rangeRows: WueTrendYearRow[],
  farmYearInputs: Record<number, FarmYearInput> | undefined,
  gUnits: GUnit[]
): Omit<
  RotationRow,
  'earlyPeriodWue' | 'latePeriodWue' | 'trend' | 'trendUnavailableReason'
> => {
  const excludedYears: RotationYearNote[] = [];
  const acc: EligibleYearAccumulator = {
    yieldKgPerHaSum: 0,
    yieldKgPerHaCount: 0,
    gmPerHaSum: 0,
    gmPerHaCount: 0,
    rainfallSum: 0,
    rainfallCount: 0,
    missingRainfallYears: [],
    managementTags: new Set(),
  };

  for (const row of rangeRows) {
    const base = row.actual ?? row.plan;
    if (!base) {
      excludedYears.push({ year: row.year, reason: 'noData' });
      continue;
    }

    const farmYear = farmYearInputs?.[row.year];
    const practiceTags = farmYear?.practiceTags ?? [];
    const exclusiveTag = EXCLUSIVE_PRACTICE_TAGS.find((tag) =>
      practiceTags.includes(tag)
    );
    if (exclusiveTag) {
      // EXCLUSIVE_PRACTICE_TAGS is only ever [fallow, pasture] — narrower
      // than PracticeTag's full type, so this reflects a real invariant.
      excludedYears.push({
        year: row.year,
        reason: exclusiveTag as 'fallow' | 'pasture',
      });
      continue;
    }

    accumulateEligibleYear(acc, row, base, areaHa, farmYear, gUnits);
  }

  for (const year of acc.missingRainfallYears) {
    excludedYears.push({ year, reason: 'missingRainfall' });
  }

  const rotationYieldKgPerHa =
    acc.yieldKgPerHaCount > 0 ? acc.yieldKgPerHaSum / acc.yieldKgPerHaCount : null;
  const rotationGmPerHa =
    acc.gmPerHaCount > 0 ? acc.gmPerHaSum / acc.gmPerHaCount : null;
  const rotationAvgRainfall =
    acc.rainfallCount > 0 ? acc.rainfallSum / acc.rainfallCount : null;

  const rotationWue =
    rotationYieldKgPerHa != null && acc.missingRainfallYears.length === 0
      ? computeWueIndex(rotationYieldKgPerHa, rotationAvgRainfall)
      : null;

  const rotationFinancialWue =
    rotationGmPerHa != null && acc.missingRainfallYears.length === 0
      ? computeFinancialWue(rotationGmPerHa, rotationAvgRainfall)
      : null;

  const managementLabels = Array.from(acc.managementTags).map(
    (tag) => FARM_DIAGNOSTICS_PRACTICE_LABELS[tag]
  );

  return {
    paddockId,
    paddockName,
    areaHa,
    managementLabels,
    excludedYears,
    rotationYieldKgPerHa,
    rotationGmPerHa,
    rotationWue,
    rotationFinancialWue,
  };
};

/**
 * Convenience wrapper combining computeRotationRow + a within-range trend
 * into the full RotationRow. Trend compares the earlier half of the
 * selected range's distinct years against the later half (e.g. a 4-year
 * range splits 2-and-2; a 5-year range splits 2-and-3) — entirely from
 * `rangeRows`, so it needs no data outside whatever's already selected and
 * shown in the Farm-Level Inputs panel above.
 */
export const computeRotationRowWithTrend = (
  paddockId: string,
  paddockName: string,
  areaHa: number,
  rangeRows: WueTrendYearRow[],
  farmYearInputs: Record<number, FarmYearInput> | undefined,
  gUnits: GUnit[]
): RotationRow => {
  const base = computeRotationRow(
    paddockId,
    paddockName,
    areaHa,
    rangeRows,
    farmYearInputs,
    gUnits
  );

  const distinctYears = Array.from(new Set(rangeRows.map((r) => r.year))).sort(
    (a, b) => a - b
  );

  let earlyPeriodWue: number | null = null;
  let latePeriodWue: number | null = null;
  let trend: TrendDirection | null = null;

  if (distinctYears.length >= 2) {
    const earlyCount = Math.floor(distinctYears.length / 2);
    const earlyYears = new Set(distinctYears.slice(0, earlyCount));
    const lateYears = new Set(distinctYears.slice(earlyCount));

    earlyPeriodWue = computeRotationRow(
      '',
      '',
      areaHa,
      rangeRows.filter((r) => earlyYears.has(r.year)),
      farmYearInputs,
      gUnits
    ).rotationWue;
    latePeriodWue = computeRotationRow(
      '',
      '',
      areaHa,
      rangeRows.filter((r) => lateYears.has(r.year)),
      farmYearInputs,
      gUnits
    ).rotationWue;

    trend = classifyTrend(latePeriodWue, earlyPeriodWue);
  }

  const trendUnavailableReason: TrendUnavailableReason | null =
    trend == null && base.rotationWue != null
      ? { kind: distinctYears.length < 2 ? 'rangeTooShort' : 'insufficientData' }
      : null;

  return {
    ...base,
    earlyPeriodWue,
    latePeriodWue,
    trend,
    trendUnavailableReason,
  };
};

export type RotationHighlight = 'topPerformer' | 'belowAverage' | 'declining' | null;

/**
 * One highlight per row, derived from the ACTUAL set of rows shown (never a
 * fixed number). Priority: `declining` (a more urgent signal) beats
 * `belowAverage`; `topPerformer` is mutually exclusive with `belowAverage`
 * by construction (the max value can't also be below the mean unless every
 * row shares the same value, in which case neither applies). Rows with no
 * `rotationWue` get no highlight — there's nothing to rank them against.
 */
export const computeRotationHighlights = (
  rows: RotationRow[]
): Record<string, RotationHighlight> => {
  const withWue = rows.filter(
    (r): r is RotationRow & { rotationWue: number } => r.rotationWue != null
  );

  const highlights: Record<string, RotationHighlight> = {};
  for (const row of rows) highlights[row.paddockId] = null;
  if (withWue.length === 0) return highlights;

  const mean = withWue.reduce((sum, r) => sum + r.rotationWue, 0) / withWue.length;
  const maxWue = Math.max(...withWue.map((r) => r.rotationWue));
  // First row reaching the max is the tie-break winner — stable, matches
  // array order (which is whatever order the caller passed in).
  const topPerformerId = withWue.find((r) => r.rotationWue === maxWue)?.paddockId;

  for (const row of withWue) {
    if (row.trend === 'declining') {
      highlights[row.paddockId] = 'declining';
    } else if (row.paddockId === topPerformerId) {
      highlights[row.paddockId] = 'topPerformer';
    } else if (row.rotationWue < mean) {
      highlights[row.paddockId] = 'belowAverage';
    }
  }

  return highlights;
};

/** Where a paddock's figure sits among the paddocks shown — only the ends are marked. */
export type RelativeStanding = 'top' | 'bottom';

export type RotationStandingColumn =
  | 'rotationWue'
  | 'rotationGmPerHa'
  | 'rotationFinancialWue';

/**
 * Marks the top and bottom third of one column among the rows shown
 * (OP-2490); the middle third is left unmarked so colour only appears where
 * there's something to say. Each column is judged on its own, so a paddock
 * can be top on WUE and bottom on margin. By rank, not value, so one
 * outlier can't skew the cut-offs; tied values share a standing (mid-rank).
 * Rows without a value, a column with fewer than two values, or one where
 * every value is the same are left unmarked — there is nothing to compare.
 */
export const computeRelativeStanding = (
  rows: RotationRow[],
  column: RotationStandingColumn
): Record<string, RelativeStanding | null> => {
  const standing: Record<string, RelativeStanding | null> = {};
  rows.forEach((row) => {
    standing[row.paddockId] = null;
  });

  const sorted = rows
    .map((row) => row[column])
    .filter((value): value is number => value != null)
    .sort((a, b) => a - b);
  const count = sorted.length;
  if (count < 2 || sorted[0] === sorted[count - 1]) return standing;

  rows.forEach((row) => {
    const value = row[column];
    if (value == null) return;
    const below = sorted.findIndex((other) => other >= value);
    const equal = sorted.filter((other) => other === value).length;
    const percentile = (below + (equal - 1) / 2) / (count - 1);
    if (percentile >= 2 / 3) standing[row.paddockId] = 'top';
    else if (percentile <= 1 / 3) standing[row.paddockId] = 'bottom';
  });

  return standing;
};
